import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { PermissionKey } from '@portal/shared';
import { useAuth, usePermission } from '../lib/auth';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Loading } from '../components/Page';
import { BrandLogo } from '../components/BrandLogo';
import { AppShell } from './AppShell';
import { LoginPage } from '../features/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage, AcceptInvitePage } from '../features/auth/PasswordPages';

const StaffDashboard = lazy(() => import('../features/dashboard/StaffDashboard'));
const ClientHome = lazy(() => import('../features/dashboard/ClientHome'));
const TicketListPage = lazy(() => import('../features/tickets/TicketListPage'));
const TicketDetailPage = lazy(() => import('../features/tickets/TicketDetailPage'));
const NewTicketPage = lazy(() => import('../features/tickets/NewTicketPage'));
const MyWorkPage = lazy(() => import('../features/tickets/MyWorkPage'));
const ProductsPage = lazy(() => import('../features/products/ProductsPage'));
const ProductDetailPage = lazy(() => import('../features/products/ProductDetailPage'));
const ClientsPage = lazy(() => import('../features/clients/ClientsPage'));
const ClientDetailPage = lazy(() => import('../features/clients/ClientDetailPage'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const RolesPage = lazy(() => import('../features/users/RolesPage'));
const WorkBoardPage = lazy(() => import('../features/work-items/WorkBoardPage'));
const ReportsPage = lazy(() => import('../features/reports/ReportsPage'));
const AuditLogPage = lazy(() => import('../features/reports/AuditLogPage'));
const SlaPoliciesPage = lazy(() => import('../features/reports/SlaPoliciesPage'));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage'));
const ProfilePage = lazy(() => import('../features/auth/ProfilePage'));

function Splash() {
  return (
    <Stack alignItems="center" justifyContent="center" gap={2.5} sx={{ minHeight: '100vh' }} role="status" aria-label="Starting">
      <BrandLogo height={34} />
      <Stack direction="row" alignItems="center" gap={1.25}>
        <CircularProgress size={16} thickness={5} />
        <Typography color="text.secondary" fontSize={14}>Starting…</Typography>
      </Stack>
    </Stack>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Guard({ perms, staff, children }: { perms?: PermissionKey[]; staff?: boolean; children: ReactNode }) {
  const { user } = useAuth();
  const can = usePermission();
  if (staff && user?.userType !== 'staff') return <Navigate to="/" replace />;
  if (perms && !can(...perms)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Sends admins without 2FA to the setup page when the API demands it. */
function MfaRedirector() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => navigate('/profile?setup2fa=1');
    window.addEventListener('portal:mfa-required', handler);
    return () => window.removeEventListener('portal:mfa-required', handler);
  }, [navigate]);
  return null;
}

/** Signed-in users visiting /login go back where they came from (deep links survive sign-in). */
function LoginRoute() {
  const { user, ready } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  if (ready && user) return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  return <LoginPage />;
}

function Home() {
  const { user } = useAuth();
  return user?.userType === 'client' ? <ClientHome /> : <StaffDashboard />;
}

export function App() {
  return (
    <>
      <MfaRedirector />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Home />} />
            <Route path="tickets" element={<TicketListPage />} />
            <Route path="tickets/new" element={<Guard perms={['ticket:create']}><NewTicketPage /></Guard>} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="my-work" element={<Guard staff><MyWorkPage /></Guard>} />
            <Route path="products" element={<Guard staff perms={['product:view']}><ProductsPage /></Guard>} />
            <Route path="products/:id" element={<Guard staff perms={['product:view']}><ProductDetailPage /></Guard>} />
            <Route path="work-items" element={<Guard staff perms={['work:view']}><WorkBoardPage /></Guard>} />
            <Route path="clients" element={<Guard staff perms={['client:view', 'client:manage']}><ClientsPage /></Guard>} />
            <Route path="clients/:id" element={<Guard staff perms={['client:view', 'client:manage']}><ClientDetailPage /></Guard>} />
            <Route path="users" element={<Guard perms={['user:manage']}><UsersPage /></Guard>} />
            <Route path="roles" element={<Guard staff perms={['role:manage']}><RolesPage /></Guard>} />
            <Route path="reports" element={<Guard perms={['report:view']}><ReportsPage /></Guard>} />
            <Route path="admin/sla" element={<Guard staff perms={['role:manage']}><SlaPoliciesPage /></Guard>} />
            <Route path="admin/audit" element={<Guard staff perms={['audit:view']}><AuditLogPage /></Guard>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
